/**
 * Premium minimalist skeleton primitives: subtle gray base + slow white sheen + optional breath motion.
 */

import type { ReactNode } from 'react';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '@hooks/useLanguage';

function SheenOverlay({ isRtl }: { isRtl: boolean }) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute inset-0 bg-skeleton-sheen bg-shimmer bg-no-repeat motion-reduce:hidden',
        isRtl ? 'motion-safe:animate-shimmer-rtl' : 'motion-safe:animate-shimmer',
      )}
      aria-hidden
    />
  );
}

/** Any shape: pass rounded + size via className (e.g. text line, circle, 16px block). */
export function SkeletonSurface({ className }: { className?: string }) {
  const { isRtl } = useLanguage();
  return (
    <div className={clsx('relative overflow-hidden bg-gray-100/50', className)} aria-hidden>
      <SheenOverlay isRtl={isRtl} />
    </div>
  );
}

/** 10px text line — pill shape (premium list / table cell). */
export function SkeletonText({ className }: { className?: string }) {
  return (
    <SkeletonSurface
      className={clsx('min-h-[10px] h-2.5 max-h-[10px] rounded-full', className)}
    />
  );
}

/** Icon / avatar — perfect circle; set size with e.g. `size-10`. */
export function SkeletonCircle({ className }: { className?: string }) {
  return <SkeletonSurface className={clsx('rounded-full aspect-square shrink-0', className)} />;
}

/** Inputs, buttons, filters — design system 16px radius. */
export function SkeletonRect({ className }: { className?: string }) {
  return <SkeletonSurface className={clsx('rounded-[16px]', className)} />;
}

/** Large blocks (charts) inside cards — 16px radius. */
export function SkeletonPanel({ className }: { className?: string }) {
  return <SkeletonSurface className={clsx('rounded-[16px]', className)} />;
}

export interface SkeletonPulseProps {
  className?: string;
}

/** Back-compat: use className for shape (prefer SkeletonText / SkeletonRect / SkeletonCircle). */
export function SkeletonPulse({ className }: SkeletonPulseProps) {
  return <SkeletonSurface className={className} />;
}

export function SkeletonShimmerPanel({ className }: SkeletonPulseProps) {
  return <SkeletonPanel className={className} />;
}

/** Soft breathing on the whole skeleton tree (Framer Motion). */
export function SkeletonBreath({ children, className }: { children: ReactNode; className?: string }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0.5 }}
      animate={{ opacity: 1 }}
      transition={
        reduceMotion
          ? undefined
          : {
              duration: 3.2,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
            }
      }
    >
      {children}
    </motion.div>
  );
}
